import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { usePage } from '@inertiajs/vue3';
import {
  ImageTypes, ListingType, MultiplePropertyListingsUserFields, MultiplePropertyTransactionUserFields, PropertyListingWrapper, PropertyTransactionWrapper, PropertyType, Status,
} from '@agentsoftware/kraken-ts-sdk/sdk/models/shared';
import { pickBy, uniqBy } from 'lodash-es';
import { KrakenSDK } from '@agentsoftware/kraken-ts-sdk';
import formattedDisplayAddress from '@/Support/FormattedDisplayAddress';
// eslint-disable-next-line import/no-cycle
import { useReportStore } from '@/Stores/Report';
import { useQueryStatus } from '@/Composables/useQueryStatus';
import pluralize from 'pluralize';
import collect from 'collect.js';
import ComparablesDetailsData = App.Data.Report.Comparables.ComparablesDetailsData;
import ComparablesTenureData = App.Data.Report.Comparables.ComparablesTenureData;
import SortType = App.Enums.SortType;
import ListingEventData = App.Data.Report.Comparables.ListingEventData;

export type ComparableTypes = 'sales' | 'lettings' | 'completed';

// eslint-disable-next-line import/prefer-default-export
export const useComparablesStore = defineStore('comparables', () => {
  const reportStore = useReportStore();

  const comparablesQueryResultsQueryStatus = useQueryStatus();
  const expandedComparablesQueryResultsQueryStatus = useQueryStatus();
  const transactionHistoryQueryStatus = useQueryStatus();

  const page = usePage();

  const kraken = computed(() => new KrakenSDK({ serverURL: page.props.config.kraken.api_url }));

  // Types
  type FilterCategories = 'value' | 'bedrooms' | 'type' | 'distance' | 'timeOnMarket' | 'status' | 'agent' | 'completedSince';

  type ExpandedFilterCategories = {
    value: boolean;
    bedrooms: boolean;
    type: boolean;
    distance: boolean;
    timeOnMarket: boolean;
    status: boolean;
    agent: boolean;
    completedSince: boolean;
  };

  type FilterOptions = {
    available: boolean;
    soldStcUnderOffer: boolean;
    letAgreed: boolean;
    reserved: boolean;
    hasReduction: boolean;
    distance: number;
    minPrice: number | null;
    maxPrice: number | null;
    minBedrooms: number | null;
    maxBedrooms: number | null;
    detached: boolean;
    semiDetached: boolean;
    terraced: boolean;
    flatsMaisonettes: boolean;
    isBungalow: boolean;
    minMonthsOnMarket: number | null;
    maxMonthsOnMarket: number | null;
    agents: { companyName: string, selected: boolean }[];
    completedSince: string | null;
  };

  type QueryFilters = {
    radius?: number | null;
    filterStatus?: string[] | null;
    filterHasReduction?: boolean;
    filterPropertyType?: string[] | null;
    filterIsBungalow?: boolean | null;
    filterMinBedrooms?: number | null;
    filterMaxBedrooms?: number | null;
    filterMinNumberOfBedrooms?: number | null;
    filterMaxNumberOfBedrooms?: number | null;
    filterMinPrice?: number | null;
    filterMaxPrice?: number | null;
    filterMinDaysOnMarket?: number;
    filterMaxDaysOnMarket?: number;
    filterAgentCompany?: string[] | null;
    filterStartMonth?: string | null;
  };

  type ComparablesState = {
    listingType: ListingType;
    comparableType: 'sales' | 'lettings' | 'completed';
    view: 'grid' | 'map';
    selectedComparablesCount: number;
    showFilters: boolean;
    filterStatuses: FilterOptions;
    queryResultsPage: number;
    queryFilters: QueryFilters | null;
    expandedFilterCategories: ExpandedFilterCategories;
    comparablesQueryResults: App.Data.Report.Comparables.ComparablesDetailsData[];
    expandedComparablesQueryResults: {
      sales: App.Data.Report.Comparables.ComparablesDetailsData[];
      lettings: App.Data.Report.Comparables.ComparablesDetailsData[];
      completed: App.Data.Report.Comparables.ComparablesDetailsData[];
    };
    comparablesQueryMaxResults: number;
    agentList: { companyName: string, selected: boolean }[];
    tenure: ComparablesTenureData | null;
    sortType: string;
    defaultDistance: number;
  };

  const state = reactive<ComparablesState>({
    listingType: ListingType.Sale,
    comparableType: 'sales',
    view: 'grid',
    selectedComparablesCount: 0,
    showFilters: false,
    filterStatuses: {
      available: true,
      letAgreed: true,
      soldStcUnderOffer: true,
      reserved: true,
      hasReduction: false,
      distance: 20,
      minPrice: null,
      maxPrice: null,
      minBedrooms: null,
      maxBedrooms: null,
      detached: false,
      semiDetached: false,
      terraced: false,
      flatsMaisonettes: false,
      isBungalow: false,
      minMonthsOnMarket: null,
      maxMonthsOnMarket: null,
      completedSince: null,
      agents: [],
    },
    queryResultsPage: 1,
    queryFilters: null,
    expandedFilterCategories: {
      value: false,
      bedrooms: false,
      type: false,
      distance: false,
      timeOnMarket: false,
      status: false,
      agent: false,
      completedSince: false,
    },
    comparablesQueryResults: [],
    expandedComparablesQueryResults: {
      sales: [],
      lettings: [],
      completed: [],
    },
    comparablesQueryMaxResults: 0,
    agentList: [],
    tenure: null,
    sortType: 'distance',
    defaultDistance: 20,
  });

  // Computed
  const comparableType = computed(() => state.comparableType);

  const propertyTypeSelected = computed(() => state.filterStatuses.detached || state.filterStatuses.semiDetached || state.filterStatuses.terraced || state.filterStatuses.flatsMaisonettes);

  const view = computed(() => state.view);

  const selectedComparablesCount = computed(() => state.selectedComparablesCount);

  const showFilters = computed(() => state.showFilters);

  const filters = computed(() => state.filterStatuses);

  const minPrice = computed(() => (state.filterStatuses.minPrice ? new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
  }).format(state.filterStatuses.minPrice) : 'No Min'));

  const maxPrice = computed(() => (state.filterStatuses.maxPrice ? new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
  }).format(state.filterStatuses.maxPrice) : 'No Max'));

  const minBedrooms = computed(() => (state.filterStatuses.minBedrooms ?? 'No Min'));

  const maxBedrooms = computed(() => (state.filterStatuses.maxBedrooms ?? 'No Max'));

  const minMonthsOnMarket = computed(() => (state.filterStatuses.minMonthsOnMarket ? state.filterStatuses.minMonthsOnMarket : 'No Min'));

  const maxMonthsOnMarket = computed(() => (state.filterStatuses.maxMonthsOnMarket ? state.filterStatuses.maxMonthsOnMarket : 'No Max'));

  const completedSince = computed(() => state.filterStatuses.completedSince);

  const comparablesQueryResults = computed(() => state.comparablesQueryResults);

  const expandedComparablesQueryResults = computed(() => state.expandedComparablesQueryResults);

  const comparablesQueryMaxResults = computed(() => state.comparablesQueryMaxResults);

  const sortType = computed(() => state.sortType);
  const defaultDistance = computed(() => state.defaultDistance);

  const setDefaultDistance = (distance: number) => {
    state.defaultDistance = distance;
  };

  const setDefaultSort = (sort: SortType) => {
    state.sortType = sort;
  };

  // Filter options
  const propertyValueRange = (start: number, end: number, interval: number) => {
    let bottomOfRange = start;
    const rangeValues = [];

    for (bottomOfRange; bottomOfRange <= end; bottomOfRange += interval) {
      rangeValues.push(bottomOfRange);
    }

    return rangeValues;
  };

  const propertyValueOptions = (type: 'min' | 'max') => {
    const valueRange = [];

    if (['sales', 'completed'].includes(comparableType.value)) {
      valueRange.push({ id: null, label: `No ${type}` });
      valueRange.push(type === 'min'
        ? { id: (reportStore.overview.propertyValue.estimatedValue ?? 0) * 0.8, label: `£${((reportStore.overview.propertyValue.estimatedValue ?? 0) * 0.8).toLocaleString()} (-20%)` }
        : { id: (reportStore.overview.propertyValue.estimatedValue ?? 0) * 1.2, label: `£${((reportStore.overview.propertyValue.estimatedValue ?? 0) * 1.2).toLocaleString()} (+20%)` });
      valueRange.push(propertyValueRange(50000, 290000, 10000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(300000, 475000, 25000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(500000, 650000, 50000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(700000, 900000, 100000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(1000000, 1750000, 250000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(2000000, 2500000, 500000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(3000000, 8000000, 1000000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(10000000, 20000000, 5000000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
    }

    if (comparableType.value === 'lettings') {
      valueRange.push({ id: null, label: `No ${type}` });
      valueRange.push(type === 'min'
        ? { id: (reportStore.overview.propertyValue.estimatedRentalValue ?? 0) * 0.8, label: `£${((reportStore.overview.propertyValue.estimatedRentalValue ?? 0) * 0.8).toLocaleString()} (-20%)` }
        : { id: (reportStore.overview.propertyValue.estimatedRentalValue ?? 0) * 1.2, label: `£${((reportStore.overview.propertyValue.estimatedRentalValue ?? 0) * 1.2).toLocaleString()} (+20%)` });
      valueRange.push(propertyValueRange(100, 500, 25).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(550, 950, 50).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(1000, 1900, 100).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(2000, 2750, 250).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(3000, 6500, 500).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(7000, 10000, 1000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(12500, 17500, 2500).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
      valueRange.push(propertyValueRange(20000, 40000, 5000).map((val) => ({ id: val, label: `£${val.toLocaleString()}` })));
    }

    const sortedValues = valueRange.flat().sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

    return uniqBy(sortedValues, 'id');
  };

  const completedSinceOptions = computed(() => {
    const currentDate = new Date();
    const twelveMonths = new Date().setMonth(currentDate.getMonth() - 12);
    const twentyFourMonths = new Date().setMonth(currentDate.getMonth() - 24);
    const thirtySixMonths = new Date().setMonth(currentDate.getMonth() - 36);
    const fortyEightMonths = new Date().setMonth(currentDate.getMonth() - 48);
    const sixtyMonths = new Date().setMonth(currentDate.getMonth() - 60);

    return [
      {
        id: `${new Date(twelveMonths).getFullYear()}-${(new Date(twelveMonths).getMonth() + 1).toLocaleString('en', { minimumIntegerDigits: 2 })}`,
        label: `${new Date(twelveMonths).toLocaleString('en', { month: 'long' })} ${new Date(twelveMonths).getFullYear()}`,
      },
      {
        id: `${new Date(twentyFourMonths).getFullYear()}-${(new Date(twentyFourMonths).getMonth() + 1).toLocaleString('en', { minimumIntegerDigits: 2 })}`,
        label: `${new Date(twentyFourMonths).toLocaleString('en', { month: 'long' })} ${new Date(twentyFourMonths).getFullYear()}`,
      },
      {
        id: `${new Date(thirtySixMonths).getFullYear()}-${(new Date(thirtySixMonths).getMonth() + 1).toLocaleString('en', { minimumIntegerDigits: 2 })}`,
        label: `${new Date(thirtySixMonths).toLocaleString('en', { month: 'long' })} ${new Date(thirtySixMonths).getFullYear()}`,
      },
      {
        id: `${new Date(fortyEightMonths).getFullYear()}-${(new Date(fortyEightMonths).getMonth() + 1).toLocaleString('en', { minimumIntegerDigits: 2 })}`,
        label: `${new Date(fortyEightMonths).toLocaleString('en', { month: 'long' })} ${new Date(fortyEightMonths).getFullYear()}`,
      },
      {
        id: `${new Date(sixtyMonths).getFullYear()}-${(new Date(sixtyMonths).getMonth() + 1).toLocaleString('en', { minimumIntegerDigits: 2 })}`,
        label: `${new Date(sixtyMonths).toLocaleString('en', { month: 'long' })} ${new Date(sixtyMonths).getFullYear()}`,
      },
    ];
  });

  const simpleRange = (maxValue: number, label?: string) => {
    const values: { id: number, label: string }[] = [];

    for (let i = 1; i <= maxValue; i += 1) {
      const formattedLabel = label ? `${i} ${pluralize(label, i)}` : i.toString();
      values.push({ id: i, label: formattedLabel });
    }

    return values;
  };

  const bedroomOptionsMin = computed(() => [{ id: null, label: 'No min' }, ...simpleRange(10)]);

  const bedroomOptionsMax = computed(() => [{ id: null, label: 'No max' }, ...simpleRange(10)]);

  const timeOnMarketOptionsMin = computed(() => [{ id: null, label: 'No min' }, ...simpleRange(36, 'month')]);

  const timeOnMarketOptionsMax = computed(() => [{ id: null, label: 'No max' }, ...simpleRange(36, 'month')]);

  const agentList = async (filterListingType: ComparableTypes | null = 'sales') => {
    if (!reportStore.location) {
      return;
    }

    const comparablesRequestParameters = pickBy({
      point: [`${reportStore.location.lat.toString()},${reportStore.location.lng.toString()}`],
      radius: 20000,
      results: 100,
      page: state.queryResultsPage,
    });

    try {
      const response = await kraken.value.areas
        .getPropertyListingsInAreaAreasPropertyListingsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            filterListingType: filterListingType === 'sales' ? 'sale' : 'rental',
            fieldsListing: [
              MultiplePropertyListingsUserFields.Address,
              MultiplePropertyListingsUserFields.Agent,
              MultiplePropertyListingsUserFields.CouncilTaxBand,
              MultiplePropertyListingsUserFields.Description,
              MultiplePropertyListingsUserFields.DisplayAddress,
              MultiplePropertyListingsUserFields.DistanceFromPoint,
              MultiplePropertyListingsUserFields.EnergyEfficiencyRating,
              MultiplePropertyListingsUserFields.EstimatedValue,
              MultiplePropertyListingsUserFields.EstimatedValueRounded,
              MultiplePropertyListingsUserFields.GardenDirection,
              MultiplePropertyListingsUserFields.HasReduction,
              MultiplePropertyListingsUserFields.Images,
              MultiplePropertyListingsUserFields.InternalAreaSquareMetres,
              MultiplePropertyListingsUserFields.IsBungalow,
              MultiplePropertyListingsUserFields.ListedDate,
              MultiplePropertyListingsUserFields.ListingEvents,
              MultiplePropertyListingsUserFields.ListingId,
              MultiplePropertyListingsUserFields.ListingQuality,
              MultiplePropertyListingsUserFields.ListingType,
              MultiplePropertyListingsUserFields.Location,
              MultiplePropertyListingsUserFields.MainImageUrl,
              MultiplePropertyListingsUserFields.NumberOfBathrooms,
              MultiplePropertyListingsUserFields.NumberOfBedrooms,
              MultiplePropertyListingsUserFields.OriginalPrice,
              MultiplePropertyListingsUserFields.OutdoorSpaceAreaSquareMetres,
              MultiplePropertyListingsUserFields.Ownership,
              MultiplePropertyListingsUserFields.Price,
              MultiplePropertyListingsUserFields.PropertyType,
              MultiplePropertyListingsUserFields.Source,
              MultiplePropertyListingsUserFields.Status,
              MultiplePropertyListingsUserFields.StreetGroupPropertyId,
              MultiplePropertyListingsUserFields.Tenure,
              MultiplePropertyListingsUserFields.WeeklyPrice,
              MultiplePropertyListingsUserFields.YearBuilt,
              MultiplePropertyListingsUserFields.YearsAtProperty,
            ],
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        );

      const parsedAgents = response?.data?.map((comparableProperty) => (
        {
          agent: comparableProperty.attributes.agent,
        })) ?? [];

      const uniqueAgents = parsedAgents.map((agent) => ({
        companyName: agent.agent?.companyName ?? '',
        selected: true,
      }))
        .filter((value) => value.companyName !== '')
        .filter((value, index, self) => self.findIndex((v) => v.companyName === value.companyName) === index)
        .sort((a, b) => a.companyName.localeCompare(b.companyName));

      state.agentList = uniqueAgents;
      state.filterStatuses.agents = uniqueAgents;
    } catch (e: any) {
      state.filterStatuses.agents = [];
    }
  };

  const selectAllAgents = computed({
    get: () => state.filterStatuses.agents.every((agent) => agent.selected),
    // eslint-disable-next-line no-return-assign,no-param-reassign
    set: (value) => state.filterStatuses.agents.forEach((agent) => agent.selected = value),
  });

  // Functions
  const formatCompletedImages = (images?: ImageTypes | null) => {
    if (!images) {
      return [];
    }

    const roomTypes = ['exterior', 'living_room', 'kitchen', 'garden', 'bedroom', 'dining_room', 'conservatory', 'bathroom'];

    const compiledImages: string[] = [];

    roomTypes.forEach((roomType) => {
      images[roomType as keyof ImageTypes]?.forEach((room) => compiledImages.push(room));
    });

    return compiledImages;
  };

  const parseComparable = (comparableProperty: PropertyListingWrapper) => ({
    id: comparableProperty.id ?? '',
    streetGroupPropertyId: comparableProperty.attributes.streetGroupPropertyId ?? null,
    listingType: comparableProperty.attributes.listingType as string,
    address: comparableProperty.attributes.address ? formattedDisplayAddress(comparableProperty.attributes.address) : comparableProperty.attributes.displayAddress as string,
    image: comparableProperty.attributes.mainImageUrl ?? '',
    listingImages: comparableProperty.attributes.images as string[],
    price: comparableProperty.attributes.price as number ?? null,
    listingEvents: comparableProperty.attributes.listingEvents?.map((listingEvent) => ({
      eventType: listingEvent.eventType,
      date: listingEvent.date.toString(),
      price: listingEvent.relatedPrice as number,
    })) ?? null as (ListingEventData[] | null),
    bedrooms: comparableProperty.attributes.numberOfBedrooms as number,
    bathrooms: comparableProperty.attributes.numberOfBathrooms as number,
    type: comparableProperty.attributes.propertyType as PropertyType,
    councilTaxBand: comparableProperty.attributes.councilTaxBand ? comparableProperty.attributes.councilTaxBand.toString() : null,
    internalArea: comparableProperty.attributes.internalAreaSquareMetres ?? null,
    internalAreaSqFt: comparableProperty.attributes.internalAreaSquareMetres ? Math.round(comparableProperty.attributes.internalAreaSquareMetres * 10.7639) : null,
    outdoorSpace: comparableProperty.attributes.outdoorSpaceAreaSquareMetres ?? null,
    outdoorSpaceSqFt: comparableProperty.attributes.outdoorSpaceAreaSquareMetres ? Math.round(comparableProperty.attributes.outdoorSpaceAreaSquareMetres * 10.7639) : null,
    pricePerSqFt: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price
      ? Math.round(comparableProperty.attributes.price / Math.round(comparableProperty.attributes.internalAreaSquareMetres * 10.7639))
      : null,
    pricePerSqM: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price
      ? Math.round(comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres)
      : null,
    distanceFromPoint: comparableProperty.attributes.distanceFromPoint ?? null,
    location: {
      lng: comparableProperty.attributes.location?.coordinates?.longitude ?? 0,
      lat: comparableProperty.attributes.location?.coordinates?.latitude ?? 0,
    },
    status: comparableProperty.attributes.status as Status,
    listedDate: comparableProperty.attributes.listedDate as string,
    transactionDate: null,
    agent: comparableProperty.attributes.agent as ComparablesDetailsData['agent'],
    tenure: comparableProperty.attributes.tenure as ComparablesDetailsData['tenure'],
  });

  const parseCompletedComparable = (comparableProperty: PropertyTransactionWrapper) => ({
    id: comparableProperty.id ?? '',
    streetGroupPropertyId: comparableProperty.attributes.streetGroupPropertyId ?? null,
    listingType: 'completed',
    address: comparableProperty.attributes.address ? formattedDisplayAddress(comparableProperty.attributes.address) : null,
    image: formatCompletedImages(comparableProperty.attributes.images)[0] ?? null,
    listingImages: formatCompletedImages(comparableProperty.attributes.images),
    price: comparableProperty.attributes.price as number ?? null,
    listingEvents: null,
    bedrooms: comparableProperty.attributes.numberOfBedrooms as number ?? null,
    bathrooms: comparableProperty.attributes.numberOfBathrooms as number ?? null,
    type: comparableProperty.attributes.propertyType as string ?? null,
    councilTaxBand: null,
    internalArea: comparableProperty.attributes.internalAreaSquareMetres ?? null,
    internalAreaSqFt: comparableProperty.attributes.internalAreaSquareMetres ? Math.round(comparableProperty.attributes.internalAreaSquareMetres * 10.7639) : null,
    outdoorSpace: null,
    outdoorSpaceSqFt: null,
    pricePerSqFt: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price
      ? Math.round(comparableProperty.attributes.price / (comparableProperty.attributes.internalAreaSquareMetres * 10.7639))
      : null,
    pricePerSqM: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price
      ? Math.round(comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres)
      : null,
    distanceFromPoint: comparableProperty.attributes.distanceInMetres ?? null,
    location: {
      lng: comparableProperty.attributes.location?.coordinates?.longitude ?? 0,
      lat: comparableProperty.attributes.location?.coordinates?.latitude ?? 0,
    },
    status: null,
    listedDate: null,
    transactionDate: comparableProperty.attributes.transactionDate?.toString() ?? null,
    agent: null,
    tenure: null,
  });

  const retrieveRelaxedComparables = async () => {
    if (!reportStore.location || expandedComparablesQueryResults.value[comparableType.value].length > 0) {
      return;
    }

    expandedComparablesQueryResultsQueryStatus.setStatus('loading');

    const comparablesRequestParameters = pickBy({
      point: [`${reportStore.location.lat},${reportStore.location.lng}`],
      results: 8,
      page: state.queryResultsPage,
      radius: 20000,
    });

    try {
      const response = comparableType.value === 'completed'
        ? await kraken.value.areas.getPropertyTransactionsInAreaAreasPropertyTransactionsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            sort: 'distance',
            fieldsTransaction: [
              MultiplePropertyTransactionUserFields.StreetGroupPropertyId,
              MultiplePropertyTransactionUserFields.Address,
              MultiplePropertyTransactionUserFields.TransactionId,
              MultiplePropertyTransactionUserFields.TransactionDate,
              MultiplePropertyTransactionUserFields.Price,
              MultiplePropertyTransactionUserFields.Location,
              MultiplePropertyTransactionUserFields.PropertyType,
              MultiplePropertyTransactionUserFields.IsNewBuild,
              MultiplePropertyTransactionUserFields.PricePerSquareMetre,
              MultiplePropertyTransactionUserFields.InternalAreaSquareMetres,
              MultiplePropertyTransactionUserFields.NumberOfBedrooms,
              MultiplePropertyTransactionUserFields.NumberOfBathrooms,
              MultiplePropertyTransactionUserFields.Images,
              MultiplePropertyTransactionUserFields.StreetView,
              MultiplePropertyTransactionUserFields.Tenure,
              MultiplePropertyTransactionUserFields.DistanceInMetres,
            ],
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        )
        : await kraken.value.areas.getPropertyListingsInAreaAreasPropertyListingsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            sort: 'distance_from_point',
            fieldsListing: [
              MultiplePropertyListingsUserFields.Address,
              MultiplePropertyListingsUserFields.Agent,
              MultiplePropertyListingsUserFields.CouncilTaxBand,
              MultiplePropertyListingsUserFields.Description,
              MultiplePropertyListingsUserFields.DisplayAddress,
              MultiplePropertyListingsUserFields.DistanceFromPoint,
              MultiplePropertyListingsUserFields.EnergyEfficiencyRating,
              MultiplePropertyListingsUserFields.EstimatedValue,
              MultiplePropertyListingsUserFields.EstimatedValueRounded,
              MultiplePropertyListingsUserFields.GardenDirection,
              MultiplePropertyListingsUserFields.HasReduction,
              MultiplePropertyListingsUserFields.Images,
              MultiplePropertyListingsUserFields.InternalAreaSquareMetres,
              MultiplePropertyListingsUserFields.IsBungalow,
              MultiplePropertyListingsUserFields.ListedDate,
              MultiplePropertyListingsUserFields.ListingEvents,
              MultiplePropertyListingsUserFields.ListingId,
              MultiplePropertyListingsUserFields.ListingQuality,
              MultiplePropertyListingsUserFields.ListingType,
              MultiplePropertyListingsUserFields.Location,
              MultiplePropertyListingsUserFields.MainImageUrl,
              MultiplePropertyListingsUserFields.NumberOfBathrooms,
              MultiplePropertyListingsUserFields.NumberOfBedrooms,
              MultiplePropertyListingsUserFields.OriginalPrice,
              MultiplePropertyListingsUserFields.OutdoorSpaceAreaSquareMetres,
              MultiplePropertyListingsUserFields.Price,
              MultiplePropertyListingsUserFields.PropertyType,
              MultiplePropertyListingsUserFields.Source,
              MultiplePropertyListingsUserFields.Status,
              MultiplePropertyListingsUserFields.StreetGroupPropertyId,
              MultiplePropertyListingsUserFields.Tenure,
              MultiplePropertyListingsUserFields.WeeklyPrice,
              MultiplePropertyListingsUserFields.YearsAtProperty,
            ],
            filterListingType: comparableType.value === 'sales' ? 'sale' : 'rental',
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        );

      const excludingExistingResults = response?.data.filter((property) => !(comparablesQueryResults.value.map((result) => result.id)).includes(property.id));

      state.expandedComparablesQueryResults[comparableType.value] = excludingExistingResults?.map((comparableProperty) => (comparableType.value === 'completed'
        ? parseCompletedComparable(comparableProperty as PropertyTransactionWrapper)
        : parseComparable(comparableProperty as PropertyListingWrapper))) ?? [];

      expandedComparablesQueryResultsQueryStatus.setStatus('success');
    } catch (e: any) {
      if (e.status === 404) {
        expandedComparablesQueryResultsQueryStatus.setStatus('success');
        return;
      }

      expandedComparablesQueryResultsQueryStatus.setStatus('error');
    }
  };

  const fetchComparables = async () => {
    if (!reportStore.location) {
      return;
    }

    comparablesQueryResultsQueryStatus.setStatus('loading');

    const comparablesRequestParameters = pickBy({
      point: [`${reportStore.location.lat},${reportStore.location.lng}`],
      results: 48,
      page: state.queryResultsPage,
      ...state.queryFilters,
    });

    try {
      const response = await kraken.value.areas
        .getPropertyListingsInAreaAreasPropertyListingsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            sort: 'distance_from_point',
            fieldsListing: [
              MultiplePropertyListingsUserFields.Address,
              MultiplePropertyListingsUserFields.Agent,
              MultiplePropertyListingsUserFields.CouncilTaxBand,
              MultiplePropertyListingsUserFields.Description,
              MultiplePropertyListingsUserFields.DisplayAddress,
              MultiplePropertyListingsUserFields.DistanceFromPoint,
              MultiplePropertyListingsUserFields.EnergyEfficiencyRating,
              MultiplePropertyListingsUserFields.EstimatedValue,
              MultiplePropertyListingsUserFields.EstimatedValueRounded,
              MultiplePropertyListingsUserFields.GardenDirection,
              MultiplePropertyListingsUserFields.HasReduction,
              MultiplePropertyListingsUserFields.Images,
              MultiplePropertyListingsUserFields.InternalAreaSquareMetres,
              MultiplePropertyListingsUserFields.IsBungalow,
              MultiplePropertyListingsUserFields.ListedDate,
              MultiplePropertyListingsUserFields.ListingEvents,
              MultiplePropertyListingsUserFields.ListingId,
              MultiplePropertyListingsUserFields.ListingQuality,
              MultiplePropertyListingsUserFields.ListingType,
              MultiplePropertyListingsUserFields.Location,
              MultiplePropertyListingsUserFields.MainImageUrl,
              MultiplePropertyListingsUserFields.NumberOfBathrooms,
              MultiplePropertyListingsUserFields.NumberOfBedrooms,
              MultiplePropertyListingsUserFields.OriginalPrice,
              MultiplePropertyListingsUserFields.OutdoorSpaceAreaSquareMetres,
              MultiplePropertyListingsUserFields.Price,
              MultiplePropertyListingsUserFields.PropertyType,
              MultiplePropertyListingsUserFields.Source,
              MultiplePropertyListingsUserFields.Status,
              MultiplePropertyListingsUserFields.StreetGroupPropertyId,
              MultiplePropertyListingsUserFields.Tenure,
              MultiplePropertyListingsUserFields.WeeklyPrice,
              MultiplePropertyListingsUserFields.YearsAtProperty,
            ],
            filterListingType: comparableType.value === 'sales' ? 'sale' : 'rental',
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        );

      const currentPropertyExcluded = response?.data.filter((property) => property.attributes.streetGroupPropertyId !== reportStore.propertyId);

      const parsedComparables = currentPropertyExcluded?.map((comparableProperty) => (parseComparable(comparableProperty))) ?? [];

      state.comparablesQueryResults = [...state.comparablesQueryResults, ...parsedComparables];

      if (comparablesQueryResults.value.length < 9) {
        retrieveRelaxedComparables();
      } else {
        state.expandedComparablesQueryResults[comparableType.value] = [];
      }

      state.comparablesQueryMaxResults = response?.meta?.total ?? 0;
      comparablesQueryResultsQueryStatus.setStatus('success');
    } catch (e: any) {
      console.log(e);
      if (e.status === 404) {
        comparablesQueryResultsQueryStatus.setStatus('success');
        retrieveRelaxedComparables();
        return;
      }

      comparablesQueryResultsQueryStatus.setStatus('error');
    }
  };

  const fetchCompletedComparables = async () => {
    if (!reportStore.location) {
      return;
    }

    comparablesQueryResultsQueryStatus.setStatus('loading');

    if (state.queryFilters && !state.queryFilters?.filterStartMonth) {
      state.queryFilters.filterStartMonth = completedSinceOptions.value[1].id;
    }

    const comparablesRequestParameters = pickBy({
      point: [`${reportStore.location.lat},${reportStore.location.lng}`],
      results: 48,
      page: state.queryResultsPage,
      ...state.queryFilters,
    });

    try {
      const response = await kraken.value.areas
        .getPropertyTransactionsInAreaAreasPropertyTransactionsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            sort: 'distance',
            fieldsTransaction: [
              MultiplePropertyTransactionUserFields.Address,
              MultiplePropertyTransactionUserFields.StreetGroupPropertyId,
              MultiplePropertyTransactionUserFields.Address,
              MultiplePropertyTransactionUserFields.TransactionId,
              MultiplePropertyTransactionUserFields.TransactionDate,
              MultiplePropertyTransactionUserFields.Price,
              MultiplePropertyTransactionUserFields.Location,
              MultiplePropertyTransactionUserFields.PropertyType,
              MultiplePropertyTransactionUserFields.IsNewBuild,
              MultiplePropertyTransactionUserFields.PricePerSquareMetre,
              MultiplePropertyTransactionUserFields.InternalAreaSquareMetres,
              MultiplePropertyTransactionUserFields.NumberOfBedrooms,
              MultiplePropertyTransactionUserFields.NumberOfBathrooms,
              MultiplePropertyTransactionUserFields.Images,
              MultiplePropertyTransactionUserFields.StreetView,
              MultiplePropertyTransactionUserFields.Tenure,
              MultiplePropertyTransactionUserFields.DistanceInMetres,
            ],
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        );

      const currentPropertyExcluded = response?.data.filter((property) => property.attributes.streetGroupPropertyId !== reportStore.propertyId);

      const parsedComparables = currentPropertyExcluded?.map((comparableProperty) => (parseCompletedComparable(comparableProperty))) ?? [];

      state.comparablesQueryResults = [...state.comparablesQueryResults, ...parsedComparables];

      if (comparablesQueryResults.value.length < 9) {
        retrieveRelaxedComparables();
      }

      state.comparablesQueryMaxResults = response?.meta?.total ?? 0;
      comparablesQueryResultsQueryStatus.setStatus('success');
    } catch (e: any) {
      console.log(e);
      if (e.status === 404) {
        comparablesQueryResultsQueryStatus.setStatus('success');
        retrieveRelaxedComparables();
        return;
      }

      comparablesQueryResultsQueryStatus.setStatus('error');
    }
  };

  // Set query filter values
  const applyPriceFilters = () => {
    state.comparablesQueryResults = [];

    state.queryFilters = {
      ...state.queryFilters,
      filterMinPrice: state.filterStatuses.minPrice,
      filterMaxPrice: state.filterStatuses.maxPrice,
    };
  };

  const applyMinBedroomsFilter = () => {
    state.comparablesQueryResults = [];
    state.queryFilters = {
      ...state.queryFilters,
      filterMinNumberOfBedrooms: state.filterStatuses.minBedrooms,
    };
  };

  const applyMaxBedroomsFilter = () => {
    state.comparablesQueryResults = [];
    state.queryFilters = {
      ...state.queryFilters,
      filterMaxNumberOfBedrooms: state.filterStatuses.maxBedrooms,
    };
  };

  const applyCompletedMinBedroomsFilter = () => {
    state.comparablesQueryResults = [];
    state.queryFilters = {
      ...state.queryFilters,
      filterMinBedrooms: state.filterStatuses.minBedrooms,
    };
  };

  const applyCompletedMaxBedroomsFilter = () => {
    state.comparablesQueryResults = [];
    state.queryFilters = {
      ...state.queryFilters,
      filterMaxBedrooms: state.filterStatuses.maxBedrooms,
    };
  };

  const applyStatusFilter = () => {
    state.comparablesQueryResults = [];

    const statusQuery: Status[] = [];

    if (state.filterStatuses.available) {
      statusQuery.push(Status.Available);
    }

    if (state.filterStatuses.reserved) {
      statusQuery.push(Status.Reserved);
    }

    if (state.filterStatuses.soldStcUnderOffer) {
      statusQuery.push(Status.SoldSTC);
      statusQuery.push(Status.UnderOffer);
    }

    if (state.filterStatuses.letAgreed) {
      statusQuery.push(Status.LetAgreed);
    }

    state.queryFilters = {
      ...state.queryFilters,
      filterStatus: statusQuery,
    };
  };

  const applyHasReductionFilter = () => {
    state.queryFilters = {
      ...state.queryFilters,
      filterHasReduction: state.filterStatuses.hasReduction,
    };
  };

  const applyPropertyTypeFilter = () => {
    state.comparablesQueryResults = [];

    const propertyTypeQuery: PropertyType[] = [];

    if (state.filterStatuses.detached) {
      propertyTypeQuery.push(PropertyType.Detached);
    }

    if (state.filterStatuses.semiDetached) {
      propertyTypeQuery.push(PropertyType.SemiDetached);
    }

    if (state.filterStatuses.terraced) {
      propertyTypeQuery.push(PropertyType.Terraced);
    }

    if (state.filterStatuses.flatsMaisonettes) {
      propertyTypeQuery.push(PropertyType.FlatsMaisonettes);
    }

    state.queryFilters = {
      ...state.queryFilters,
      filterPropertyType: propertyTypeQuery,
    };
  };

  const applyIsBungalowFilter = () => {
    state.comparablesQueryResults = [];

    state.queryFilters = {
      ...state.queryFilters,
      filterIsBungalow: state.filterStatuses.isBungalow,
    };
  };

  const applyDistanceFilter = () => {
    state.comparablesQueryResults = [];

    state.queryFilters = {
      ...state.queryFilters,
      radius: state.filterStatuses.distance * 1000,
    };
  };

  const applyTimeOnMarketFilters = () => {
    state.comparablesQueryResults = [];

    state.queryFilters = {
      ...state.queryFilters,
      filterMinDaysOnMarket: Number((state.filterStatuses.minMonthsOnMarket ?? 0) * 30),
      filterMaxDaysOnMarket: Number((state.filterStatuses.maxMonthsOnMarket ?? 36) * 30),
    };
  };

  const applyAgentFilters = () => {
    state.comparablesQueryResults = [];

    if (selectAllAgents.value) {
      state.queryFilters = {
        ...state.queryFilters,
        filterAgentCompany: null,
      };

      return;
    }

    const agentQuery: string[] = [];

    state.filterStatuses.agents.forEach((agent) => {
      if (agent.selected) {
        agentQuery.push(agent.companyName);
      }
    });

    state.queryFilters = {
      ...state.queryFilters,
      filterAgentCompany: agentQuery.length === 0 ? null : agentQuery,
    };
  };

  const applyCompletedSinceFilters = () => {
    state.comparablesQueryResults = [];

    state.queryFilters = {
      ...state.queryFilters,
      filterStartMonth: state.filterStatuses.completedSince ?? completedSinceOptions.value[1].id,
    };
  };

  const toggleShowFilters = (showFilterStatus: boolean) => {
    state.showFilters = showFilterStatus;
  };

  const setView = (comparablesView: 'grid' | 'map') => {
    state.view = comparablesView;
  };

  type QueryPropertyType = 'detached' | 'semiDetached' | 'terraced' | 'flatsMaisonettes';

  const getPropertyTypeFilter = (): QueryPropertyType[] => {
    if (!reportStore.overview?.build?.propertyType) {
      return ['detached', 'semiDetached', 'terraced', 'flatsMaisonettes'];
    }

    const propertyType: { [key: string]: QueryPropertyType; } = {
      Detached: 'detached',
      'Semi-Detached': 'semiDetached',
      Terraced: 'terraced',
      'Flats/Maisonettes': 'flatsMaisonettes',
    };

    return [propertyType[reportStore.overview.build.propertyType]];
  };

  const applySearchResultFilters = async () => {
    state.queryResultsPage = 1;
    state.comparablesQueryResults = [];

    applyDistanceFilter();
    applyMinBedroomsFilter();
    applyMaxBedroomsFilter();
    applyPriceFilters();
    applyStatusFilter();
    applyHasReductionFilter();
    applyPropertyTypeFilter();
    applyIsBungalowFilter();
    applyTimeOnMarketFilters();
    applyAgentFilters();

    await fetchComparables();
  };

  const applyCompletedSearchResultFilters = async () => {
    state.queryResultsPage = 1;
    state.comparablesQueryResults = [];

    applyDistanceFilter();
    applyCompletedMinBedroomsFilter();
    applyCompletedMaxBedroomsFilter();
    applyPriceFilters();
    applyPropertyTypeFilter();
    applyCompletedSinceFilters();

    await fetchCompletedComparables();
  };

  const toggleFilterCategory = (filterCategory: FilterCategories) => {
    state.expandedFilterCategories[filterCategory] = !state.expandedFilterCategories[filterCategory];
  };

  const filterCategoryExpanded = (filterCategory: FilterCategories) => state.expandedFilterCategories[filterCategory];

  const removeSearchResultFilters = async () => {
    state.queryFilters = {
      radius: 2000,
    };

    state.agentList = state.agentList.map((a) => ({ companyName: a.companyName, selected: true }));

    state.filterStatuses = {
      available: false,
      letAgreed: false,
      reserved: false,
      soldStcUnderOffer: false,
      distance: state.defaultDistance,
      minPrice: null,
      maxPrice: null,
      hasReduction: false,
      minBedrooms: null,
      maxBedrooms: null,
      detached: true,
      semiDetached: true,
      terraced: true,
      flatsMaisonettes: true,
      isBungalow: false,
      minMonthsOnMarket: null,
      maxMonthsOnMarket: null,
      completedSince: null,
      agents: state.agentList,
    };

    fetchComparables();
  };

  const sortedComparablesSelection = computed(() => {
    const { comparables } = reportStore.comparables[comparableType.value];

    return comparables?.sort((a, b) => (a.distanceFromPoint ?? 0) - (b.distanceFromPoint ?? 0));
  });

  const updateSelectedComparablesCount = () => {
    state.selectedComparablesCount = sortedComparablesSelection.value?.length ?? 0;
  };

  const loadMoreResults = async () => {
    state.queryResultsPage += 1;

    if (state.comparableType === 'completed') {
      await fetchCompletedComparables();
    } else {
      await fetchComparables();
    }

    updateSelectedComparablesCount();
  };

  const initialiseDefaults = async () => {
    if (comparableType.value === 'lettings') {
      state.filterStatuses.minPrice = reportStore.overview.propertyValue.estimatedRentalValue ? reportStore.overview.propertyValue.estimatedRentalValue * 0.8 : null;
      state.filterStatuses.maxPrice = reportStore.overview.propertyValue.estimatedRentalValue ? reportStore.overview.propertyValue.estimatedRentalValue * 1.2 : null;
    } else {
      state.filterStatuses.minPrice = reportStore.overview.propertyValue.estimatedValue ? reportStore.overview.propertyValue.estimatedValue * 0.8 : null;
      state.filterStatuses.maxPrice = reportStore.overview.propertyValue.estimatedValue ? reportStore.overview.propertyValue.estimatedValue * 1.2 : null;
    }

    state.expandedFilterCategories.value = true;
    state.expandedFilterCategories.type = true;

    state.filterStatuses.distance = state.defaultDistance;

    const propertyType = getPropertyTypeFilter();

    propertyType.forEach((type) => {
      state.filterStatuses[type] = true;
    });

    if (comparableType.value !== 'completed') {
      agentList(comparableType.value);
    }

    if (comparableType.value === 'completed') {
      state.expandedFilterCategories.completedSince = true;
      state.filterStatuses.completedSince = completedSinceOptions.value[1].id;
      await applyCompletedSearchResultFilters();
    } else {
      await applySearchResultFilters();
    }
  };

  const updateComparableType = async (sectionId: 'sales' | 'lettings' | 'completed' | null) => {
    if (!sectionId) {
      return;
    }

    comparablesQueryResultsQueryStatus.setStatus('loading');

    state.comparableType = sectionId;

    await initialiseDefaults();
  };

  const filterCount = computed(() => {
    const availableFilters = state.queryFilters;

    if (!availableFilters) {
      return 0;
    }

    const appliedFilters = {
      status: availableFilters.filterStatus && (availableFilters.filterStatus.length < 5 || availableFilters.filterHasReduction),
      price: !!(availableFilters.filterMinPrice || availableFilters.filterMaxPrice),
      numberOfBedrooms: !!(availableFilters.filterMinNumberOfBedrooms || availableFilters.filterMaxNumberOfBedrooms),
      propertyType: !availableFilters.filterPropertyType || availableFilters.filterPropertyType.length < 4,
      distance: availableFilters.radius && availableFilters.radius < 20000,
      timeOnMarket: comparableType.value !== 'completed'
        && (!availableFilters.filterMinDaysOnMarket || availableFilters.filterMinDaysOnMarket > 0)
        && (!availableFilters.filterMaxDaysOnMarket || availableFilters.filterMaxDaysOnMarket < 1095),
      agents: availableFilters.filterAgentCompany ? (availableFilters.filterAgentCompany.length < state.agentList.length) : false,
      completedSince: comparableType.value === 'completed' && Number(availableFilters.filterStartMonth) > Number(new Date().getFullYear() - 5),
    };

    return Object.values(appliedFilters).filter((filter) => filter).length;
  });

  const requestTransactionHistory = async (streetGroupPropertyId: string) => {
    transactionHistoryQueryStatus.setStatus('loading');

    try {
      const response = await kraken.value.properties.getPropertyDetailsPropertiesStreetGroupPropertyIdGet(
        { xApiKey: page.props.config.kraken.api_key },
        ['transactions'],
        streetGroupPropertyId,
        { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
      );
      transactionHistoryQueryStatus.setStatus('success');
      return response.data.attributes.transactions ?? [];
    } catch (e: any) {
      transactionHistoryQueryStatus.setStatus('success');
      return [];
    }
  };

  const removeAllSelected = () => {
    reportStore.state.content.comparables[comparableType.value].comparables = [];
  };

  const comparableSectionOrder = computed(() => [
    {
      id: 'sales', name: 'Sales', display: reportStore.comparables.sales.display, order: reportStore.comparables.sales.order, styles: 'text-default-700 bg-emerald-100 hover:bg-emerald-200 border-emerald-600',
    },
    {
      id: 'completed', name: 'Completed', display: reportStore.comparables.completed.display, order: reportStore.comparables.completed.order, styles: 'text-default-700 bg-red-100 hover:bg-red-200 border-red-700',
    },
    {
      id: 'lettings', name: 'Lettings', display: reportStore.comparables.lettings.display, order: reportStore.comparables.lettings.order, styles: 'text-default-700 bg-violet-100 hover:bg-violet-200 border-violet-600',
    },
  ].filter((component) => component.display).sort((a, b) => (a.order - b.order)));

  return {
    agentList,
    applySearchResultFilters,
    applyCompletedSearchResultFilters,
    comparablesQueryMaxResults,
    comparablesQueryResults,
    comparablesQueryResultsQueryStatus,
    comparableSectionOrder,
    comparableType,
    completedSince,
    defaultDistance,
    expandedComparablesQueryResults,
    expandedComparablesQueryResultsQueryStatus,
    fetchComparables,
    formatCompletedImages,
    filterCategoryExpanded,
    filters,
    filterCount,
    filterOptions: {
      bedroomOptionsMin,
      bedroomOptionsMax,
      completedSinceOptions,
      propertyValueOptions,
      timeOnMarketOptionsMin,
      timeOnMarketOptionsMax,
    },
    loadMoreResults,
    maxBedrooms,
    maxPrice,
    minBedrooms,
    minPrice,
    minMonthsOnMarket,
    maxMonthsOnMarket,
    propertyTypeSelected,
    removeAllSelected,
    removeSearchResultFilters,
    requestTransactionHistory,
    selectAllAgents,
    setDefaultDistance,
    setDefaultSort,
    setView,
    showFilters,
    selectedComparablesCount,
    sortedComparablesSelection,
    sortType,
    state,
    transactionHistoryQueryStatus,
    toggleFilterCategory,
    toggleShowFilters,
    updateComparableType,
    updateSelectedComparablesCount,
    view,
  };
});
